import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CARD, CARD2, CARD3, BD, TX, TX2, GR, BL, YL, RD, PU } from "../../utils/constants";
import { scoreColor, gradeLabel, biasColor, fmt, getInvestmentAdvice, sanitizeDecimalInput } from "../../utils/formatUtils";

function useCountUp(target, duration = 600) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    setCount(0);
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return count;
}

function MiniChart({ stock }) {
  const change = Number(stock.change || 0);
  const fiveDay = Number(stock.fiveDayReturn ?? change);
  const twentyDay = Number(stock.twentyDayReturn ?? fiveDay);
  const seed = stock.symbol.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0);
  const points = Array.from({ length: 18 }, (_, i) => {
    const t = i / 17;
    const trend = (twentyDay / 100) * t;
    const pulse = Math.sin((t * Math.PI * 3.2) + seed) * 0.018;
    const lateMove = (fiveDay / 100) * Math.max(0, t - 0.66) * 1.4;
    return 100 * (1 + trend + pulse + lateMove);
  });
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = Math.max(max - min, 1);
  const chartPoints = points.map((value, i) => {
    const x = 18 + (i / (points.length - 1)) * 524;
    const y = 178 - ((value - min) / range) * 130;
    return { x, y };
  });
  const line = chartPoints.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  const area = `${line} L 542 190 L 18 190 Z`;
  const lineColor = change >= 0 ? GR : RD;
  const gradientId = `chartFill${stock.symbol.replace(/[^a-zA-Z0-9]/g, "")}`;

  return (
    <div style={{ borderRadius: 10, overflow: "hidden", border: `1px solid ${BD}`, background: CARD2, marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 13px", borderBottom: `1px solid ${BD}` }}>
        <div>
          <div style={{ fontSize: 8, color: TX2, letterSpacing: 2, marginBottom: 3 }}>SCANNER CHART</div>
          <div style={{ fontSize: 10, color: TX }}>Local trend view for research context</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 8, color: TX2, letterSpacing: 1 }}>5-DAY</div>
          <div style={{ fontSize: 14, color: fiveDay >= 0 ? GR : RD, fontWeight: 800 }}>{fiveDay >= 0 ? "+" : ""}{fiveDay.toFixed(2)}%</div>
        </div>
      </div>
      <svg viewBox="0 0 560 210" role="img" aria-label={`${stock.symbol} scanner trend chart`} style={{ width: "100%", height: 230, display: "block" }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={lineColor} stopOpacity="0.24" />
            <stop offset="100%" stopColor={lineColor} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {[42, 80, 118, 156].map((y) => (
          <line key={y} x1="18" x2="542" y1={y} y2={y} stroke={BD} strokeOpacity="0.72" strokeDasharray="4 7" />
        ))}
        {[18, 150, 280, 410, 542].map((x) => (
          <line key={x} x1={x} x2={x} y1="28" y2="190" stroke={BD} strokeOpacity="0.38" />
        ))}
        <path d={area} fill={`url(#${gradientId})`} />
        <path d={line} fill="none" stroke={lineColor} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        {chartPoints.filter((_, i) => i % 4 === 0 || i === chartPoints.length - 1).map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="4" fill={CARD} stroke={lineColor} strokeWidth="2" />
        ))}
        <text x="20" y="24" fill={TX2} fontSize="10" letterSpacing="2">20D RANGE</text>
        <text x="392" y="24" fill={lineColor} fontSize="12" fontWeight="700">{twentyDay >= 0 ? "+" : ""}{twentyDay.toFixed(2)}% 20D</text>
        <text x="20" y="205" fill={TX2} fontSize="9">Educational visualization. Verify on live charts before making decisions.</text>
      </svg>
    </div>
  );
}


export default function StockDetailModal({
  stock,
  onClose,
  watchlist,
  onToggleWL,
  alerts, setAlerts,
  alertInput, setAlertInput,
  newsMap,
  loadingDetail,
  investments,
  onAddInvestment,
  onRemoveInvestment,
}) {
  const displayScore = useCountUp(stock?.score || 0, 500);
  const isMobile = useMemo(() => window.innerWidth < 768, []);
  const [investPrice, setInvestPrice] = useState("");
  useEffect(() => { setInvestPrice(""); }, [stock?.symbol]);

  if (!stock) return null;
  return (
    <AnimatePresence>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="ts-scan-shell"
      style={{ position: "fixed", inset: 0, background: "rgba(5,8,20,0.86)", display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center", zIndex: 1000, padding: isMobile ? 0 : 16 }}
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 60, opacity: 0, scale: 0.97 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ duration: 0.22, ease: "easeOut" }}
        className="ts-panel"
        role="dialog"
        aria-modal="true"
        aria-label={`${stock.symbol} detail`}
        style={{ background: CARD, border: `1px solid ${scoreColor(stock.score)}40`, borderRadius: isMobile ? "16px 16px 0 0" : 14, padding: 22, maxWidth: isMobile ? "100vw" : 620, width: "100%", maxHeight: isMobile ? "95vh" : "93vh", overflowY: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
              <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 24, fontWeight: 800, color: TX }}>{stock.symbol}</div>
              <span className="star" onClick={() => onToggleWL(stock.symbol)} style={{ fontSize: 16, color: watchlist.includes(stock.symbol) ? YL : TX2 }}>{watchlist.includes(stock.symbol) ? "★" : "☆"}</span>
            </div>
            <div style={{ fontSize: 10, color: TX2 }}>{stock.name} · {stock.sector}</div>
            <div style={{ fontSize: 12, color: TX, marginTop: 4 }}>${stock.price?.toFixed(2)} <span style={{ color: stock.change >= 0 ? GR : RD, fontWeight: 600 }}>{fmt(stock.change || 0)}</span></div>
            {stock.fiveDayReturn != null && (
              <div style={{ fontSize: 9, color: TX2, marginTop: 2 }}>
                5-day: <span style={{ color: stock.fiveDayReturn >= 0 ? GR : RD, fontWeight: 600 }}>{stock.fiveDayReturn >= 0 ? "+" : ""}{stock.fiveDayReturn.toFixed(2)}%</span>
                {stock.twentyDayReturn != null && (<>{"  "}20-day: <span style={{ color: stock.twentyDayReturn >= 0 ? GR : RD, fontWeight: 600 }}>{stock.twentyDayReturn >= 0 ? "+" : ""}{stock.twentyDayReturn.toFixed(2)}%</span></>)}
              </div>
            )}
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 32, fontWeight: 800, fontFamily: "'Space Grotesk',sans-serif", color: scoreColor(stock.score), lineHeight: 1 }}>{displayScore}</div>
            <div style={{ fontSize: 8, color: scoreColor(stock.score), letterSpacing: 2, marginTop: 2 }}>{gradeLabel(stock.score)}</div>
            <div style={{ fontSize: 9, marginTop: 4, color: biasColor(stock.tomorrowBias), fontWeight: 600 }}>Tomorrow: {stock.tomorrowBias}</div>
            {loadingDetail && <div style={{ fontSize: 8, color: TX2, marginTop: 4 }}>loading details...</div>}
          </div>
        </div>

        <MiniChart stock={stock} />

        {/* Why the algorithm picked this */}
        <div style={{ background: CARD2, borderRadius: 10, padding: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 9, color: TX2, letterSpacing: 2, marginBottom: 12 }}>WHY THE ALGORITHM PICKED THIS</div>
          {[
            { label: "MOMENTUM", value: stock.momentum, color: PU, reasons: stock.momentumReasons },
            { label: "POSITION IN RANGE", value: stock.volumeScore, color: BL, reasons: stock.volumeReasons },
            { label: "TREND", value: stock.trend, color: GR, reasons: stock.trendReasons },
          ].map(({ label, value, color, reasons }) => (
            <div key={label} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                <span style={{ fontSize: 9, color, fontWeight: 600, letterSpacing: 1 }}>{label}</span>
                <span style={{ fontSize: 13, fontWeight: 800, color }}>{value}<span style={{ fontSize: 9, color: TX2 }}>/100</span></span>
              </div>
              <div style={{ height: 4, background: CARD3, borderRadius: 3, marginBottom: 6 }}>
                <div style={{ height: "100%", width: `${value}%`, background: color, borderRadius: 3 }} />
              </div>
              {reasons?.map((r, i) => (
                <div key={i} style={{ fontSize: 9, color: TX2, padding: "3px 8px", background: CARD3, borderRadius: 4, marginBottom: 3, borderLeft: `2px solid ${color}50` }}>{r}</div>
              ))}
            </div>
          ))}

          {stock.candleData && (
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", padding: "8px 10px", background: CARD3, borderRadius: 6 }}>
              {stock.candleData.ma5  && <span style={{ fontSize: 8, color: TX2 }}>MA5:  <span style={{ color: TX }}>${stock.candleData.ma5}</span></span>}
              {stock.candleData.ma20 && <span style={{ fontSize: 8, color: TX2 }}>MA20: <span style={{ color: TX }}>${stock.candleData.ma20}</span></span>}
              {stock.candleData.ma50 && <span style={{ fontSize: 8, color: TX2 }}>MA50: <span style={{ color: TX }}>${stock.candleData.ma50}</span></span>}
              {stock.fiveDayReturn   != null && <span style={{ fontSize: 8, color: TX2 }}>5-day: <span style={{ color: stock.fiveDayReturn >= 0 ? GR : RD }}>{stock.fiveDayReturn >= 0 ? "+" : ""}{stock.fiveDayReturn.toFixed(2)}%</span></span>}
              {stock.twentyDayReturn != null && <span style={{ fontSize: 8, color: TX2 }}>20-day: <span style={{ color: stock.twentyDayReturn >= 0 ? GR : RD }}>{stock.twentyDayReturn >= 0 ? "+" : ""}{stock.twentyDayReturn.toFixed(2)}%</span></span>}
            </div>
          )}

          {stock.extraReasons?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 9, color: TX2, letterSpacing: 2, marginBottom: 6 }}>EXTRA SIGNALS</div>
              {stock.extraReasons.map((r, i) => {
                const isRisk = r.includes("EARNINGS RISK") || r.includes("selling");
                const isGood = r.includes("buying") || r.includes("Post-earnings") || r.includes("growing");
                const col    = isRisk ? RD : isGood ? GR : YL;
                return (
                  <div key={i} style={{ fontSize: 9, color: TX2, padding: "3px 8px", background: CARD3, borderRadius: 4, marginBottom: 3, borderLeft: `2px solid ${col}` }}>{r}</div>
                );
              })}
            </div>
          )}
        </div>

        {/* Risk / Reward */}
        {stock.riskReward && (
          <div style={{ background: CARD2, borderRadius: 10, padding: 14, marginBottom: 14 }}>
            <div style={{ fontSize: 9, color: TX2, letterSpacing: 2, marginBottom: 11 }}>RISK / REWARD SETUP (SWING)</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
              {[{ l: "ENTRY", v: `$${stock.riskReward.entry?.toFixed(2)}`, c: TX }, { l: "STOP LOSS", v: `$${stock.riskReward.stopLoss?.toFixed(2)}`, c: RD }, { l: "TARGET", v: `$${stock.riskReward.target?.toFixed(2)}`, c: GR }, { l: "R:R RATIO", v: `1:${stock.riskReward.rr}`, c: YL }].map((item) => (
                <div key={item.l} style={{ background: CARD3, borderRadius: 7, padding: "9px 10px" }}>
                  <div style={{ fontSize: 7, color: TX2, letterSpacing: 1, marginBottom: 3 }}>{item.l}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: item.c }}>{item.v}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Day stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 7, marginBottom: 14 }}>
          {[{ l: "DAY HIGH", v: `$${(stock.high || 0).toFixed(2)}` }, { l: "DAY LOW", v: `$${(stock.low || 0).toFixed(2)}` }, { l: "PREV CLOSE", v: `$${(stock.prevClose || 0).toFixed(2)}` }].map((item) => (
            <div key={item.l} style={{ background: CARD2, borderRadius: 7, padding: "7px 10px" }}>
              <div style={{ fontSize: 7, color: TX2, letterSpacing: 1, marginBottom: 2 }}>{item.l}</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: TX }}>{item.v}</div>
            </div>
          ))}
        </div>

        {/* My Investment */}
        <div style={{ background: CARD2, borderRadius: 10, padding: 13, marginBottom: 13, border: `1px solid ${BL}18` }}>
          <div style={{ fontSize: 8, color: BL, letterSpacing: 2, marginBottom: 9, fontWeight: 600 }}>MY INVESTMENT</div>
          {(() => {
            const inv = investments?.[stock.symbol];
            if (!inv) {
              return (
                <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder={`Price you paid (e.g. ${(stock.price || 0).toFixed(2)})`}
                    value={investPrice}
                    onChange={(e) => setInvestPrice(sanitizeDecimalInput(e.target.value))}
                    style={{ flex: 1, background: CARD3, border: `1px solid ${BD}`, borderRadius: 5, padding: "7px 9px", color: TX, fontSize: 10 }}
                  />
                  <button
                    className="btn"
                    onClick={() => { if (investPrice) { onAddInvestment(stock.symbol, investPrice); setInvestPrice(""); } }}
                    style={{ background: BL + "18", border: `1px solid ${BL}40`, borderRadius: 5, padding: "7px 13px", color: BL, fontSize: 9, fontWeight: 700 }}
                  >
                    Track
                  </button>
                </div>
              );
            }
            const advice = getInvestmentAdvice(inv.entryPrice, stock.price, stock);
            const verdictColor = advice.verdict === "SELL" ? RD : GR;
            return (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 9 }}>
                  <div>
                    <div style={{ fontSize: 8, color: TX2 }}>YOUR ENTRY PRICE</div>
                    <div style={{ fontSize: 13, color: TX, fontWeight: 700 }}>${inv.entryPrice.toFixed(2)}</div>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: advice.pnlPct >= 0 ? GR : RD }}>
                    {advice.pnlPct >= 0 ? "+" : ""}{advice.pnlPct.toFixed(2)}%
                  </div>
                </div>
                <div style={{ padding: "9px 11px", background: verdictColor + "12", border: `1px solid ${verdictColor}40`, borderRadius: 7 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: verdictColor, letterSpacing: 1, marginBottom: 3 }}>{advice.verdict}</div>
                  <div style={{ fontSize: 9, color: TX2, lineHeight: 1.5 }}>{advice.reason}</div>
                </div>
                <button className="btn" onClick={() => onRemoveInvestment(stock.symbol)} style={{ width: "100%", marginTop: 8, background: CARD3, border: `1px solid ${BD}`, borderRadius: 6, padding: "7px", color: TX2, fontSize: 8, letterSpacing: 1 }}>
                  STOP TRACKING
                </button>
              </div>
            );
          })()}
        </div>

        {/* Price alert */}
        <div style={{ background: CARD2, borderRadius: 9, padding: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 8, color: TX2, letterSpacing: 2, marginBottom: 7 }}>PRICE ALERT</div>
          <div style={{ display: "flex", gap: 7 }}>
            <input type="text" inputMode="decimal" placeholder={`Target (e.g. $${((stock.price || 0) * 1.05).toFixed(0)})`} value={alertInput} onChange={(e) => setAlertInput(sanitizeDecimalInput(e.target.value))} style={{ flex: 1, background: CARD3, border: `1px solid ${BD}`, borderRadius: 5, padding: "6px 9px", color: TX, fontSize: 10 }} />
            <button className="btn" onClick={() => { if (alertInput) { setAlerts((p) => ({ ...p, [stock.symbol]: alertInput })); setAlertInput(""); } }} style={{ background: GR + "18", border: `1px solid ${GR}40`, borderRadius: 5, padding: "6px 12px", color: GR, fontSize: 9, fontWeight: 700 }}>Set</button>
            {alerts[stock.symbol] && <button className="btn" onClick={() => setAlerts((p) => { const n = { ...p }; delete n[stock.symbol]; return n; })} style={{ background: RD + "12", border: `1px solid ${RD}40`, borderRadius: 5, padding: "6px 9px", color: RD, fontSize: 9 }}>✕</button>}
          </div>
          {alerts[stock.symbol] && <div style={{ fontSize: 8, color: YL, marginTop: 5 }}>Alert at ${alerts[stock.symbol]}</div>}
        </div>

        {/* Do Your Research */}
        {stock.score >= 75 && (
          <div style={{ background: CARD2, border: `1px solid ${stock.score === 100 ? RD + "60" : YL + "40"}`, borderRadius: 9, padding: 12, marginBottom: 12 }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: stock.score === 100 ? RD : YL, letterSpacing: 1, marginBottom: 9 }}>
              {stock.score === 100 ? "EXTREME SIGNAL - VERIFY BEFORE ACTING" : "DO YOUR RESEARCH FIRST"}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {[
                { label: "Google News", color: BL, href: `https://news.google.com/search?q=${stock.symbol}+stock` },
                { label: "Seeking Alpha", color: GR, href: `https://seekingalpha.com/symbol/${stock.symbol}` },
                { label: "Reddit WSB", color: "#ff6314", href: `https://www.reddit.com/r/wallstreetbets/search/?q=${stock.symbol}&sort=new` },
                { label: "Earnings Whispers", color: YL, href: `https://www.earningswhispers.com/stocks/${stock.symbol.toLowerCase()}` },
              ].map((link) => (
                <a key={link.label} href={link.href} target="_blank" rel="noreferrer"
                  style={{ display: "block", textAlign: "center", padding: "7px 10px", background: CARD3, border: `1px solid ${BD}`, borderRadius: 6, color: link.color, fontSize: 9, fontWeight: 600, textDecoration: "none", letterSpacing: 0.5 }}>
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* News */}
        {newsMap[stock.symbol]?.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 8, color: TX2, letterSpacing: 2, marginBottom: 7 }}>LATEST NEWS</div>
            {newsMap[stock.symbol].map((n, i) => (
              <a key={i} href={n.url} target="_blank" rel="noreferrer" style={{ display: "block", fontSize: 9, color: BL, marginBottom: 4, textDecoration: "none", padding: "6px 9px", background: CARD2, borderRadius: 5, borderLeft: `2px solid ${BL}40` }}>
                {(n.headline || "").slice(0, 90)}...
              </a>
            ))}
          </div>
        )}

        {/* External links */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 7, marginBottom: 10 }}>
          <a href={`https://finance.yahoo.com/quote/${stock.symbol}`} target="_blank" rel="noreferrer" style={{ textAlign: "center", background: BL + "10", border: `1px solid ${BL}30`, borderRadius: 7, padding: "8px", color: BL, textDecoration: "none", fontSize: 9, fontWeight: 600 }}>Yahoo Finance</a>
          <a href={`https://www.tradingview.com/chart/?symbol=${stock.symbol}`} target="_blank" rel="noreferrer" style={{ textAlign: "center", background: GR + "10", border: `1px solid ${GR}30`, borderRadius: 7, padding: "8px", color: GR, textDecoration: "none", fontSize: 9, fontWeight: 600 }}>TradingView</a>
          <a href={`https://stockanalysis.com/stocks/${stock.symbol.toLowerCase()}/`} target="_blank" rel="noreferrer" style={{ textAlign: "center", background: YL + "10", border: `1px solid ${YL}30`, borderRadius: 7, padding: "8px", color: YL, textDecoration: "none", fontSize: 9, fontWeight: 600 }}>Stock Analysis</a>
        </div>

        <button className="btn" onClick={onClose} style={{ width: "100%", background: CARD2, border: `1px solid ${BD}`, borderRadius: 7, padding: "8px", color: TX2, fontSize: 8, letterSpacing: 2 }}>CLOSE</button>
      </motion.div>
    </motion.div>
    </AnimatePresence>
  );
}
